import { type Rng } from '@/core/rng';

export interface Move {
  id: string;
  name: string;
  basePower: number;
  accuracy: number;
}

export interface Combatant {
  name: string;
  maxHp: number;
  hp: number;
  attack: number;
  defense: number;
  moves: ReadonlyArray<Move>;
}

export type CombatSide = 'player' | 'enemy';
export type BattleStatus = 'pending-action' | 'end-victory' | 'end-defeat';

export type BattleEvent =
  | { kind: 'attack'; attacker: CombatSide; target: CombatSide; move: string }
  | { kind: 'damage'; target: CombatSide; amount: number }
  | { kind: 'miss'; target: CombatSide }
  | { kind: 'end'; winner: CombatSide };

export interface BattleState {
  player: Combatant;
  enemy: Combatant;
  whoseTurn: CombatSide;
  status: BattleStatus;
  turn: number;
  log: BattleEvent[];
  rng: Rng;
}

export const STRIKE: Move = {
  id: 'strike',
  name: 'Strike',
  basePower: 8,
  accuracy: 0.95,
};

export const PLAYER_DEFAULT_MOVES: ReadonlyArray<Move> = [STRIKE];

const cloneCombatant = (c: Combatant): Combatant => ({ ...c, moves: c.moves });

const cloneState = (s: BattleState): BattleState => ({
  player: cloneCombatant(s.player),
  enemy: cloneCombatant(s.enemy),
  whoseTurn: s.whoseTurn,
  status: s.status,
  turn: s.turn,
  log: [...s.log],
  rng: s.rng,
});

const opposite = (side: CombatSide): CombatSide => (side === 'player' ? 'enemy' : 'player');

export const createBattle = (player: Combatant, enemy: Combatant, rng: Rng): BattleState => ({
  player: cloneCombatant(player),
  enemy: cloneCombatant(enemy),
  whoseTurn: 'player',
  status: 'pending-action',
  turn: 1,
  log: [],
  rng,
});

// Pure step: the current side acts with the chosen move (defaults to its
// first canonical move) and the state advances. Returns the input
// unchanged if the battle is already over.
export const executeMove = (state: BattleState, moveIndex = 0): BattleState => {
  if (state.status !== 'pending-action') return state;

  const next = cloneState(state);
  const attackerSide = next.whoseTurn;
  const targetSide = opposite(attackerSide);
  const attacker = attackerSide === 'player' ? next.player : next.enemy;
  const target = targetSide === 'player' ? next.player : next.enemy;
  const moves = attacker.moves;
  if (moves.length === 0) throw new Error(`combatant ${attacker.name} has no moves`);
  const idx = moveIndex >= 0 && moveIndex < moves.length ? moveIndex : 0;
  const move = moves[idx]!;

  next.log.push({ kind: 'attack', attacker: attackerSide, target: targetSide, move: move.id });

  if (next.rng.float() > move.accuracy) {
    next.log.push({ kind: 'miss', target: targetSide });
  } else {
    const base = attacker.attack * move.basePower * 0.1;
    const variance = next.rng.range(0.85, 1.15);
    const raw = Math.max(1, Math.floor((base - target.defense * 0.5) * variance));
    target.hp = Math.max(0, target.hp - raw);
    next.log.push({ kind: 'damage', target: targetSide, amount: raw });
  }

  if (next.player.hp <= 0) {
    next.status = 'end-defeat';
    next.log.push({ kind: 'end', winner: 'enemy' });
  } else if (next.enemy.hp <= 0) {
    next.status = 'end-victory';
    next.log.push({ kind: 'end', winner: 'player' });
  } else {
    next.whoseTurn = targetSide;
    next.turn += 1;
  }

  return next;
};
